/** API client for the memory frame (spec §6, §7.4): the globe on open and refresh, one focus view per selection, bundle pages,
 * thing search, one fact and fact edits. Shapes follow Part 05 "Response shapes". */
import type { ApiError } from "./client";
import { apiRequest } from "./client";
import { withQuery } from "./memory";
interface ApiResponse<T> {
  status: "success";
  data: T;
}
/** The focus endpoint's alias for the user's own thing, and the key the store and the globe use for it. */
export const YOU = "you";
export type ThingKind =
  | "user_profile"
  | "person"
  | "company"
  | "project"
  | "resource"
  | "account"
  | "document"
  | "concept"
  | "category";
export type EpistemicStatus = "fact" | "goal" | "decision" | "hypothesis";
export type QualifierKind = "applies_to" | "activity" | "audience";
/** A qualifier's target; a catalog activity is named by its title and has the kind "activity". */
export interface MemoryRef {
  id: string;
  name: string;
  kind: string;
}
export interface ProfileFields {
  role: string;
  focus: string;
  audience: string;
  working_style: string;
  business_url: string;
}
/** `id` is null only for a thing a pending suggestion would create; `profile` is only on You (stored name "profile"). */
export interface MemoryThing {
  id: string | null;
  name: string;
  kind: ThingKind;
  description: string;
  created_at?: string;
  updated_at?: string;
  profile?: ProfileFields;
}
export interface FactEnd {
  id: string;
  name: string;
  kind: ThingKind;
}
export interface FactQualifier {
  kind: QualifierKind;
  target: MemoryRef;
}
export interface MemoryFact {
  id: string;
  source: FactEnd;
  target: FactEnd;
  relationship_type: string;
  claim: string;
  epistemic_status: EpistemicStatus;
  scope_state: string;
  qualifiers: FactQualifier[];
  created_at: string;
  updated_at: string;
}
export interface SuggestionEnd {
  id: string | null;
  name: string;
  kind: ThingKind;
}
/** A pending fact or thing candidate; `endpoints` are a fact's source and target, or the one thing an entity names. */
export interface MemorySuggestion {
  id: string;
  type: "fact" | "entity";
  reason: string;
  claim: string;
  relationship_type: string | null;
  endpoints: SuggestionEnd[];
  current_fact_id: string | null;
}
export interface GlobeThing {
  thing: MemoryThing;
  more_count: number;
}
/** You first, then at most 60 things by degree; `more_count` ("+N") counts connections that are not drawn. */
export interface GlobeView {
  memory_size: number;
  things: GlobeThing[];
  facts: MemoryFact[];
  suggestions: MemorySuggestion[];
}
export interface RingSlot {
  thing: MemoryThing;
  facts: MemoryFact[];
  more_count: number;
  suggestion_ids: string[];
}
export interface BundleSummary {
  kind: ThingKind;
  count: number;
}
/** `focus` is null only for "you" before the You thing exists; `ring` and `bundles` together stay within 12. */
export interface FocusView {
  focus: MemoryThing | null;
  memory_size: number | null;
  ring: RingSlot[];
  bundles: BundleSummary[];
  suggestions: MemorySuggestion[];
}
export interface Page<T> {
  items: T[];
  pagination: { has_more: boolean; next_cursor: string | null };
}
export interface BundleItem {
  thing: MemoryThing;
  facts: MemoryFact[];
}
/** A user's edit of a fact (spec §7.2): the claim, its status, the qualifiers it keeps. */
export interface FactPatch {
  claim?: string;
  epistemic_status?: EpistemicStatus;
  qualifiers?: Array<{ kind: QualifierKind; target_id: string }>;
}
async function read<T>(url: string): Promise<T> {
  return (await apiRequest<ApiResponse<T>>(url, { silent: true })).data;
}
export function getGlobe(projectId?: string): Promise<GlobeView> {
  return read(withQuery("/api/v1/memory/globe/", { project_id: projectId }));
}
export function getFocus(
  focus: string,
  projectId?: string,
): Promise<FocusView> {
  return read(
    withQuery("/api/v1/memory/focus/", { node: focus, project_id: projectId }),
  );
}
/** One page of a bundle; `focusId` is a thing id (You's id, never the alias). */
export function getBundle(
  focusId: string,
  kind: ThingKind,
  projectId?: string,
  q?: string,
  cursor?: string,
): Promise<Page<BundleItem>> {
  return read(
    withQuery(`/api/v1/memory/focus/${focusId}/bundle/`, {
      kind,
      q: q?.trim() || undefined,
      cursor,
      project_id: projectId,
    }),
  );
}
export function searchThings(
  q: string,
  cursor?: string,
): Promise<Page<MemoryThing>> {
  return read(withQuery("/api/v1/memory/search/", { q: q.trim(), cursor }));
}
export function getFact(id: string): Promise<MemoryFact> {
  return read(`/api/v1/memory/facts/${id}/`);
}
export async function editFact(
  id: string,
  patch: FactPatch,
): Promise<MemoryFact> {
  return (
    await apiRequest<ApiResponse<MemoryFact>>(`/api/v1/memory/facts/${id}/`, {
      method: "PATCH",
      body: patch,
      silent: true,
    })
  ).data;
}
export async function removeFact(id: string): Promise<void> {
  await apiRequest(`/api/v1/memory/facts/${id}/`, {
    method: "DELETE",
    silent: true,
  });
}
/** The edit would land on another active fact's slot (`edge_lifecycle.set_qualifiers`, Part 05 Task 3). */
export function isFactExistsError(error: unknown): boolean {
  const apiError = error as Partial<ApiError> | null;
  return apiError?.status === 409 && apiError.data?.code === "fact_exists";
}
